// apps/web/src/layouts/components/BreadcrumbHost.tsx

import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMatches, useNavigate } from 'react-router-dom';

import { Breadcrumb, Skeleton } from '@alamo/ui';
import type { BreadcrumbItem } from '@alamo/ui';

import { useBreadcrumbLastLabelOverride } from './BreadcrumbContext';

/**
 * Per-route breadcrumb resolver. Each route in `routes.tsx` can declare a
 * `handle.breadcrumb` field — either a static string (common case), a
 * function that receives params+data and resolves to a label at render time,
 * or a function that resolves to an array of crumbs when the page wants to
 * inject one or more intermediate links (ex.: `Painel > Fila de análise >
 * PROP-XXXX · Nome`). Returning `null`/`undefined` from the function signals
 * "still loading" — the host then renders a Skeleton placeholder in the label
 * slot.
 */
export interface BreadcrumbExtraCrumb {
  label: string;
  /** When omitted, the crumb is rendered as plain text (no link). */
  href?: string;
}

type BreadcrumbResolver =
  | string
  | ((
      params: Readonly<Record<string, string>>,
      data: unknown,
    ) => string | BreadcrumbExtraCrumb[] | null);

interface HandleWithBreadcrumb {
  breadcrumb?: BreadcrumbResolver;
}

interface BreadcrumbSection {
  labelKey: string;
  prefixes: string[];
}

interface BreadcrumbSubsection {
  labelKey: string;
  prefixes: string[];
}

const BREADCRUMB_SECTIONS: BreadcrumbSection[] = [
  // More specific prefixes come first so payment gateway stays under Finance
  // instead of falling through to the broader Governance settings section.
  {
    labelKey: 'admin:sidebar.groups.financeiro',
    prefixes: [
      '/admin/settings/payment-gateway',
      '/admin/pending-payouts',
      '/admin/payment-onboarding',
      '/admin/payment-incidents',
      '/admin/reactivation-billing',
      '/admin/comissoes',
      '/admin/store',
    ],
  },
  {
    labelKey: 'admin:sidebar.groups.comunicacao',
    prefixes: ['/admin/marketing', '/admin/comunicados'],
  },
  {
    labelKey: 'admin:sidebar.groups.mensageria',
    prefixes: ['/admin/notifications', '/admin/notification-settings', '/admin/webhooks'],
  },
  {
    labelKey: 'admin:sidebar.groups.produtos_precos',
    prefixes: [
      '/admin/vehicle-catalog',
      '/admin/pricing',
      '/admin/pricing-tables',
      '/admin/geography',
      '/admin/public-links',
    ],
  },
  {
    labelKey: 'admin:sidebar.groups.pessoas_rede',
    prefixes: [
      '/users',
      '/parties',
      '/admin/network',
      '/admin/consultants',
      '/admin/trainings',
      '/admin/video-library',
      '/admin/dashboard/onboarding',
      '/admin/lead-distribution',
      '/admin/contratos',
      '/admin/settings/tour',
    ],
  },
  {
    labelKey: 'admin:sidebar.groups.governanca',
    prefixes: ['/permissions', '/admin/settings', '/audit', '/admin/privacy'],
  },
  {
    labelKey: 'admin:sidebar.groups.servicos',
    prefixes: [
      '/admin/suppliers',
      '/admin/tracking',
      '/admin/fulfillment-rules',
      '/admin/faq',
      '/admin/support',
    ],
  },
  {
    labelKey: 'admin:sidebar.groups.operacao',
    prefixes: [
      '/admin/registrations',
      '/admin/legal-terms',
      '/admin/migrations',
      '/admin/propostas',
      '/admin/competitor-offers',
      '/admin/vistoria',
      '/admin/documentos',
      '/admin/coverage-map',
      '/admin/proposal-map',
      '/admin/provisional-quotations',
      '/admin/quotation-exceptions',
      '/admin/policies',
      '/admin/analistas',
      '/admin/kpis',
      '/admin/sala-de-guerra',
      '/relatorios',
    ],
  },
  {
    labelKey: 'common:sidebar.section_trabalho',
    prefixes: [
      '/quotes',
      '/quotations',
      '/proposals',
      '/agenda',
      '/competitor-offers',
      '/fleet-quotations',
      '/admin/fleet-quotations',
    ],
  },
  {
    labelKey: 'common:sidebar.section_inicio',
    prefixes: ['/my-registration', '/profile', '/security', '/my-data', '/consents', '/migrate'],
  },
  {
    labelKey: 'common:sidebar.section_carteira',
    prefixes: ['/portfolio', '/associates', '/endossos'],
  },
  {
    labelKey: 'common:sidebar.section_rede',
    prefixes: ['/team', '/ranking', '/commissions', '/referrals'],
  },
  {
    labelKey: 'common:sidebar.section_gestao',
    prefixes: ['/metricas', '/metas'],
  },
  {
    labelKey: 'common:sidebar.section_recursos',
    prefixes: ['/ecommerce', '/novidades', '/training', '/my-site', '/challenge'],
  },
  {
    labelKey: 'common:sidebar.section_ajuda',
    prefixes: ['/support'],
  },
];

const BREADCRUMB_SUBSECTIONS: Record<string, BreadcrumbSubsection[]> = {
  'admin:sidebar.groups.operacao': [
    {
      labelKey: 'admin:sidebar.groups.cadastros_aprovacoes',
      prefixes: ['/admin/registrations', '/admin/legal-terms', '/admin/migrations'],
    },
    {
      labelKey: 'admin:sidebar.groups.analise_propostas',
      prefixes: [
        '/admin/propostas',
        '/admin/analistas',
        '/admin/kpis',
        '/admin/sala-de-guerra',
        '/admin/policies',
      ],
    },
    {
      labelKey: 'admin:sidebar.groups.validacoes_cobertura',
      prefixes: [
        '/admin/competitor-offers',
        '/admin/coverage-map',
        '/admin/proposal-map',
        '/admin/suggestions',
      ],
    },
    {
      labelKey: 'admin:sidebar.groups.vistoria_documentos',
      prefixes: ['/admin/vistoria', '/admin/documentos'],
    },
    { labelKey: 'reports:menu.group', prefixes: ['/relatorios'] },
  ],
  'admin:sidebar.groups.pessoas_rede': [
    {
      labelKey: 'admin:sidebar.groups.cadastros',
      prefixes: ['/users', '/admin/contratos'],
    },
    {
      labelKey: 'admin:sidebar.groups.rede',
      prefixes: ['/admin/network', '/admin/consultants', '/admin/lead-distribution'],
    },
    {
      labelKey: 'admin:sidebar.groups.aprendizado',
      prefixes: [
        '/admin/trainings',
        '/admin/video-library',
        '/admin/dashboard/onboarding',
        '/admin/settings/tour',
      ],
    },
  ],
  'admin:sidebar.groups.produtos_precos': [
    {
      labelKey: 'admin:sidebar.groups.precificacao',
      prefixes: ['/admin/pricing', '/admin/pricing-tables', '/admin/geography'],
    },
  ],
  'admin:sidebar.groups.financeiro': [
    {
      labelKey: 'admin:sidebar.groups.pagamentos',
      prefixes: [
        '/admin/settings/payment-gateway',
        '/admin/pending-payouts',
        '/admin/payment-incidents',
        '/admin/reactivation-billing',
        '/admin/payment-onboarding',
      ],
    },
    { labelKey: 'commissions:menu.group', prefixes: ['/admin/comissoes'] },
    { labelKey: 'admin:sidebar.groups.loja', prefixes: ['/admin/store'] },
  ],
  'admin:sidebar.groups.comunicacao': [
    {
      labelKey: 'admin:sidebar.groups.marketing',
      prefixes: ['/admin/marketing', '/admin/comunicados'],
    },
  ],
  'admin:sidebar.groups.governanca': [
    {
      labelKey: 'admin:sidebar.groups.configuracao',
      prefixes: ['/permissions', '/admin/settings'],
    },
    {
      labelKey: 'admin:sidebar.groups.privacidade',
      prefixes: ['/audit', '/admin/privacy'],
    },
  ],
  'admin:sidebar.groups.servicos': [
    {
      labelKey: 'admin:sidebar.groups.fornecedores',
      prefixes: ['/admin/suppliers', '/admin/tracking', '/admin/fulfillment-rules'],
    },
    { labelKey: 'admin:sidebar.groups.faq', prefixes: ['/admin/faq'] },
    { labelKey: 'support:tickets.admin.menu_group', prefixes: ['/admin/support'] },
  ],
};

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function navigationForPathname(pathname: string):
  | {
      section: BreadcrumbSection;
      subsection?: BreadcrumbSubsection;
    }
  | undefined {
  const section = BREADCRUMB_SECTIONS.find((section) =>
    section.prefixes.some((prefix) => matchesPrefix(pathname, prefix)),
  );
  if (!section) return undefined;
  const subsection = BREADCRUMB_SUBSECTIONS[section.labelKey]?.find((candidate) =>
    candidate.prefixes.some((prefix) => matchesPrefix(pathname, prefix)),
  );
  return { section, subsection };
}

interface ResolvedCrumb {
  pathname: string;
  /** null = still loading (resolver returned null) — host renders skeleton */
  label: string | null;
  /**
   * Optional href override. When a resolver returns an array of crumbs, each
   * non-terminal crumb gets `href` here so the Breadcrumb primitive renders
   * a link instead of plain text.
   */
  href?: string;
}

/**
 * Breadcrumb driver — wraps the @alamo/ui Breadcrumb primitive around the
 * route match chain (react-router v6 `useMatches()`) + per-route `handle.breadcrumb`.
 *
 * Behavior (locks from UI-SPEC section "Breadcrumb global (D-11)"):
 * - Filters matches with a `handle.breadcrumb` defined, walks them in order
 * - First item is always the home label ("Painel") linked to /dashboard
 * - Last item has no href (`aria-current="page"` handled by Breadcrumb primitive)
 * - When the resolver returns null (dynamic breadcrumb with data still loading),
 *   the label slot renders a Skeleton in the desktop view and a skeleton in
 *   the mobile current-page label.
 * - Renders nothing when the resolved list has <= 1 crumb (e.g. /dashboard)
 * - Mobile (<640px) shows a "Voltar" affordance + truncated last label
 * - Desktop (>=640px) shows the full trail via the existing @alamo/ui Breadcrumb
 */
export function BreadcrumbHost() {
  const matches = useMatches();
  const { t } = useTranslation(['common', 'admin', 'reports', 'commissions', 'support']);
  const navigate = useNavigate();
  // Runtime override do label do último crumb (ex.: AnalystDetailPage seta
  // "PROP-XXXX · Nome do Segurado" depois que o query carrega).
  const lastLabelOverride = useBreadcrumbLastLabelOverride();

  const resolved: ResolvedCrumb[] = matches.flatMap<ResolvedCrumb>((m) => {
    const resolver = (m.handle as HandleWithBreadcrumb | undefined)?.breadcrumb;
    if (resolver == null) return [];
    const value =
      typeof resolver === 'function'
        ? resolver(m.params as Readonly<Record<string, string>>, m.data)
        : resolver;
    if (Array.isArray(value)) {
      // Page-defined trail (ex.: "Fila de análise > PROP-XXXX · Nome"). Last
      // crumb anchors at the match pathname so BreadcrumbHost still treats
      // it as the current page; intermediate crumbs use the href the page
      // provided (link target).
      return value.map<ResolvedCrumb>((crumb, idx) => ({
        pathname: idx === value.length - 1 ? m.pathname : (crumb.href ?? m.pathname),
        label: crumb.label,
        href: idx === value.length - 1 ? undefined : crumb.href,
      }));
    }
    return [{ pathname: m.pathname, label: value }];
  });

  // Nothing to show on /dashboard pure (or zero-crumb routes)
  if (resolved.length === 0) return null;

  const home: BreadcrumbItem = { label: t('common:breadcrumb.home'), href: '/dashboard' };

  // Map resolved crumbs to BreadcrumbItems — skip the home slot if the first
  // resolved crumb is itself /dashboard (avoid "Painel > Painel")
  const trail: ResolvedCrumb[] =
    resolved[0]?.pathname === '/dashboard' ? resolved.slice(1) : resolved;

  if (trail.length === 0) return null;

  const navigation = navigationForPathname(trail[0]?.pathname ?? '');
  const sectionItem: BreadcrumbItem | null = navigation
    ? { label: t(navigation.section.labelKey) }
    : null;
  const subsectionLabel = navigation?.subsection ? t(navigation.subsection.labelKey) : null;
  const subsectionItem: BreadcrumbItem | null =
    subsectionLabel && trail[0]?.label !== subsectionLabel ? { label: subsectionLabel } : null;

  const items: BreadcrumbItem[] = [
    home,
    ...(sectionItem ? [sectionItem] : []),
    ...(subsectionItem ? [subsectionItem] : []),
    ...trail.map<BreadcrumbItem>((c, idx) => {
      const isLast = idx === trail.length - 1;
      // Override do último crumb tem prioridade sobre o label estático/resolver.
      const baseLabel = c.label ?? t('common:breadcrumb.loading');
      const label = isLast && lastLabelOverride ? lastLabelOverride : baseLabel;
      return {
        // Breadcrumb primitive renders label as plain text — when still loading
        // we pass the i18n "Carregando..." sentinel; mobile view renders an
        // actual <Skeleton /> so both modalities show a loading affordance.
        label,
        // Page-defined trails carry an explicit href on intermediate crumbs;
        // route-default crumbs link to the match pathname.
        href: isLast ? undefined : (c.href ?? c.pathname),
      };
    }),
  ];

  const lastResolved = trail[trail.length - 1];
  if (!lastResolved) return null;
  const isLastLoading = lastResolved.label === null && !lastLabelOverride;
  const lastLabel = lastLabelOverride ?? lastResolved.label;

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <>
      {/* Mobile: only last label + back button */}
      <nav
        className="sm:hidden flex items-center gap-2 min-w-0"
        aria-label={t('common:breadcrumb.aria')}
      >
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm font-body text-slate-600 hover:text-secondary-950 transition-colors"
        >
          <ChevronLeft className="size-4" aria-hidden />
          {t('common:breadcrumb.back')}
        </button>
        <span className="text-sm font-body font-bold text-secondary-950 truncate max-w-[200px]">
          {isLastLoading ? <Skeleton className="h-4 w-24 inline-block" /> : (lastLabel as string)}
        </span>
      </nav>

      {/* Desktop: full breadcrumb trail */}
      <Breadcrumb items={items} className="min-w-0" />
    </>
  );
}
